/* eslint-disable @next/next/no-img-element */
"use client";

import { useEffect, useRef, useState } from "react";
import { LocaleLink } from "@/components/shared/LocaleLink";
import { usePathname, useRouter } from "next/navigation";
import { AccountMenuButton } from "@/components/layout/AccountMenuButton";
import { NotificationsBell } from "@/components/layout/NotificationsBell";
import { useAuth } from "@/contexts/AuthContext";
import { useMobileMenu } from "@/contexts/MobileMenuContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { SITE_LOGO_ALT, SITE_LOGO_SRC } from "@/lib/siteLogo";
import { NavCitySelector } from "@/components/layout/NavCitySelector";
import { NavLoyaltyPoints } from "@/components/layout/NavLoyaltyPoints";
import { megaMenuItemsByKey, MegaIcon, MegaMenuDropdown } from "@/components/layout/NavMegaMenu";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";
import { useIsMdBreakpoint } from "@/hooks/useIsMdBreakpoint";
import { LoginDialog } from "@/components/shared/LoginDialog";
import { withLocalePrefix } from "@/lib/i18nRouting";
import { NavLanguageSwitcher } from "@/components/layout/NavLanguageSwitcher";

function NavLoginOrAccount({
  loginClassName,
  onLoginClick,
  openLoginDialog,
}: {
  loginClassName: string;
  onLoginClick?: () => void;
  /** When set, opens `LoginDialog` instead of navigating to `/login`. */
  openLoginDialog?: () => void;
}) {
  const { isLoggedIn, isReady } = useAuth();
  const { t } = useLanguage();
  if (!isReady) {
    const compact = !loginClassName.includes("px-6");
    return (
      <span
        className={`inline-block shrink-0 rounded-lg bg-[#EFEFF0] animate-pulse ${
          compact ? "h-9 w-[4.5rem]" : "h-10 w-[5.75rem]"
        }`}
        aria-hidden
      />
    );
  }
  if (isLoggedIn) return <AccountMenuButton />;
  if (openLoginDialog) {
    return (
      <button
        type="button"
        className={loginClassName}
        onClick={() => {
          onLoginClick?.();
          openLoginDialog();
        }}
      >
        {t("nav.login")}
      </button>
    );
  }
  return (
    <LocaleLink href="/login" className={loginClassName} onClick={onLoginClick}>
      {t("nav.login")}
    </LocaleLink>
  );
}

const SCROLL_THRESHOLD = 80;
const SCROLL_RANGE = 120; // Pixels over which merge animates
/** Hysteresis so `merged` does not flip when scroll position hovers at the threshold (avoids flicker / layout thrash). */
const MERGE_PROGRESS_ON = 0.12;
const MERGE_PROGRESS_OFF = 0.04;
const menuItemKeys = [
  { key: "home", href: "/" },
  { key: "services", href: "/services" },
  { key: "offers", href: "/offers" },
  { key: "news", href: "/news" },
  { key: "about", href: "/about" },
  { key: "contact", href: "/contact" },
];

function MobileNavChevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`h-5 w-5 shrink-0 text-[#9CA3AF] transition-transform duration-200 ${expanded ? "-rotate-180" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function Header() {
  const router = useRouter();
  const pathname = usePathname();
  const { isLoggedIn, isReady, logout, user } = useAuth();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [merged, setMerged] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileNavExpandedKey, setMobileNavExpandedKey] = useState<string | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const { open, isOpen: mobileMenuOpen, close: closeMobileMenu } = useMobileMenu();
  const { locale, t, isRtl } = useLanguage();
  const isMd = useIsMdBreakpoint();
  const openNavLoginDialog = () => setLoginDialogOpen(true);
  const desktopMegaCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const clearDesktopMegaCloseTimeout = () => {
    if (!desktopMegaCloseTimeoutRef.current) return;
    clearTimeout(desktopMegaCloseTimeoutRef.current);
    desktopMegaCloseTimeoutRef.current = null;
  };
  const openDesktopMega = (key: string) => {
    clearDesktopMegaCloseTimeout();
    setActiveDropdown(key);
  };
  const scheduleDesktopMegaClose = () => {
    clearDesktopMegaCloseTimeout();
    desktopMegaCloseTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
      desktopMegaCloseTimeoutRef.current = null;
    }, 120);
  };
  /** Matches desktop nav row `paddingBottom`; offsets bar + mega into this inset so both meet the padded bottom edge */
  const desktopNavPaddingBottomPx = merged ? 12 : 8;

  useEffect(() => {
    if (!mobileMenuOpen) {
      queueMicrotask(() => {
        setMobileNavExpandedKey(null);
      });
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    return () => {
      clearDesktopMegaCloseTimeout();
    };
  }, []);

  useEffect(() => {
    let raf = 0;
    const handleScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const progress = Math.min(
          1,
          Math.max(0, (y - SCROLL_THRESHOLD) / SCROLL_RANGE)
        );
        setScrollProgress(progress);
        setMerged((prev) =>
          prev ? progress > MERGE_PROGRESS_OFF : progress >= MERGE_PROGRESS_ON
        );
      });
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /** Drawer uses `md:hidden`; if viewport crosses `md` while menu is open, state can stay true and lock scroll on desktop. */
  useEffect(() => {
    if (isMd && mobileMenuOpen) closeMobileMenu();
  }, [isMd, mobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    if (mobileMenuOpen && !isMd) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen, isMd]);

  return (
    <>
      {/* Top bar - scrolls away naturally */}
      <div id="hide-in-mobile-app">
      <header className="w-full bg-white border-b border-[#E5E5E5]">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between gap-2 sm:gap-6 py-3">
            <button
              type="button"
              onClick={() => {
                open();
                PushDataToGTM(GTMEvents.DataLayerUpdate, {
                  pageName: "MOBILE HEADER",
                  pageCategory: "MOBILE MENU",
                  eventCategory: GTMSubEvents.Click,
                  eventLabel: "MOBILE HEADER > OPEN MENU",
                });
              }}
              className="md:hidden p-2 -ml-2 rounded-lg text-[#130F0E] hover:bg-[#F4F4F4] transition-colors shrink-0"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center shrink-0 flex-1 md:flex-initial justify-center md:justify-start">
              <LocaleLink href="/" className="flex items-center" onClick={() => {
                PushDataToGTM(GTMEvents.DataLayerUpdate, {
                  pageName: !isMd ? "MOBILE HEADER" : "DESKTOP HEADER",
                  pageCategory: !isMd ? "MOBILE HEADER" : "DESKTOP HEADER",
                  eventCategory: GTMSubEvents.Click,
                  eventLabel: !isMd ? "MOBILE HEADER > LOGO CLICK" : "DESKTOP HEADER > LOGO CLICK",
                });
              }}>
                <img
                  src={SITE_LOGO_SRC}
                  alt={SITE_LOGO_ALT}
                  className="h-10 w-auto max-w-[min(100%,360px)] object-contain object-left"
                  width={360}
                  height={56}
                />
              </LocaleLink>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 flex-1 justify-end max-w-3xl">
              {isReady && (!isLoggedIn || user?.customerId) ? (
                <div className="hidden sm:flex shrink-0">
                  <NavLoyaltyPoints />
                </div>
              ) : null}
              <div className="hidden md:flex items-center gap-6 shrink-0">
                <NavCitySelector />
                <NavLanguageSwitcher analyticsPageName="DESKTOP HEADER" />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <NotificationsBell variant="nav" />
                <NavLoginOrAccount
                  loginClassName="bg-[#BD2226] text-white font-semibold text-sm px-6 py-2.5 rounded-lg hover:bg-[#C00322] transition-colors shrink-0 inline-block text-center cursor-pointer"
                  openLoginDialog={openNavLoginDialog}
                  onLoginClick={() => {
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      pageName: !isMd ? "MOBILE HEADER" : "DESKTOP HEADER",
                      pageCategory: !isMd ? "MOBILE HEADER" : "DESKTOP HEADER",
                      eventCategory: GTMSubEvents.Click,
                      eventLabel: !isMd ? "MOBILE HEADER > LOGIN CLICK" : "DESKTOP HEADER > LOGIN CLICK",
                    });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Menu bar - sticky, merges with logo when scrolled (parallax effect) */}
      <nav
        className={`sticky top-0 z-50 w-full overflow-visible bg-white border-b border-[#E5E5E5] transition-shadow duration-300 ease-out ${
          merged ? "shadow-md" : "shadow-sm"
        }`}
      >
        <div className="mx-auto max-w-[1320px] overflow-visible px-4 sm:px-6">
          {/* Mobile: City & Language - fades out as scroll progresses */}
          <div
            className="md:hidden flex w-full min-w-0 items-center gap-4 py-3 transition-[opacity,padding-block] duration-300 ease-out"
            style={{
              opacity: merged ? 0 : 1,
              height: merged ? 0 : undefined,
              overflow: merged ? "hidden" : undefined,
              paddingTop: merged ? 0 : undefined,
              paddingBottom: merged ? 0 : undefined,
            }}
          >
            {isReady && (!isLoggedIn || user?.customerId) ? (
              <div className="shrink-0">
                <NavLoyaltyPoints compact />
              </div>
            ) : null}
            <div className="min-w-0 shrink">
              <NavCitySelector />
            </div>
            <NavLanguageSwitcher analyticsPageName="MOBILE HEADER" />
          </div>

          {/* Desktop: logo + menu — row keeps bottom padding; bar/mega offset by same px so strip + panel align with padded bottom */}
          <div
            className="hidden md:flex items-stretch gap-8 overflow-visible transition-[padding-block] duration-300 ease-out"
            style={{
              paddingTop: merged ? 12 : 8,
              paddingBottom: desktopNavPaddingBottomPx,
            }}
          >
            <div
              className="flex shrink-0 items-center overflow-hidden transition-[width,margin-inline-end,opacity] duration-200 ease-out"
              style={{
                width: Math.round(scrollProgress * 220),
                marginRight: merged ? 16 : 0,
                opacity: scrollProgress,
              }}
            >
              <LocaleLink 
              href="/" 
              className="flex items-center min-w-0"
              onClick={() => {
                PushDataToGTM(GTMEvents.DataLayerUpdate, {
                  pageName: "DESKTOP HEADER",
                  pageCategory: "DESKTOP HEADER",
                  eventCategory: GTMSubEvents.Click,
                  eventLabel: "DESKTOP HEADER > LOGO CLICK",
                });
              }}
              >
                <img
                  src={SITE_LOGO_SRC}
                  alt={SITE_LOGO_ALT}
                  className="h-8 w-auto max-w-[260px] object-contain object-left"
                  width={260}
                  height={40}
                />
              </LocaleLink>
            </div>
            {menuItemKeys.map((item) => {
              const megaItems = megaMenuItemsByKey[item.key] ?? [];
              const hasMega = megaItems.length > 0;
              const isOpen = activeDropdown === item.key;
              const barVisible = hasMega ? isOpen : false;

              return (
                <div
                  key={item.key}
                  className="group relative flex min-h-full shrink-0 flex-col justify-end"
                  {...(hasMega
                    ? {
                        onMouseEnter: () => openDesktopMega(item.key),
                        onMouseLeave: scheduleDesktopMegaClose,
                      }
                    : {})}
                >
                  {/* Per-item bottom padding above the active bar (absolute bottom-0 on this column) */}
                  <div className="relative z-[1] shrink-0 pb-2">
                    {hasMega ? (
                      <button
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        className={`cursor-pointer border-0 bg-transparent p-0 text-start text-[15px] font-normal transition-colors ${isOpen ? "text-[#BD2226]" : "text-[#3A3A3C] group-hover:text-[#BD2226]"}`}
                      >
                        {t(`nav.${item.key}`)}
                      </button>
                    ) : (
                      <LocaleLink
                        onClick={() => {
                          PushDataToGTM(GTMEvents.DataLayerUpdate, {
                            pageName: "DESKTOP HEADER",
                            pageCategory: "DESKTOP HEADER",
                            eventCategory: GTMSubEvents.Click,
                          eventLabel: `DESKTOP HEADER > ${item.key.toUpperCase()}`,
                          });
                        }}
                        href={item.href}
                        className="block text-[15px] font-normal text-[#3A3A3C] transition-colors group-hover:text-[#BD2226]"
                      >
                        {t(`nav.${item.key}`)}
                      </LocaleLink>
                    )}
                  </div>
                  {/* 4px strip: bottom edge flush with row padding box (negative inset = row paddingBottom) */}
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute inset-x-0 z-[3] block h-0.5 origin-[center_bottom] rounded-none bg-[#BD2226] transition-transform duration-300 ease-out motion-reduce:transition-none ${barVisible ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
                    style={{ bottom: `-${desktopNavPaddingBottomPx}px` }}
                  />
                  {hasMega ? (
                    <MegaMenuDropdown
                      items={megaItems}
                      open={isOpen}
                      isRtl={isRtl}
                      navRowPaddingBottomPx={desktopNavPaddingBottomPx}
                      t={t}
                      menuLabel={t(`nav.${item.key}`)}
                      onMouseEnter={() => openDesktopMega(item.key)}
                      onMouseLeave={scheduleDesktopMegaClose}
                    />
                  ) : null}
                </div>
              );
            })}
            <div className="flex-1" style={{ minWidth: merged ? 16 : 0 }} />
            <div
              className="flex shrink-0 items-center gap-4 self-stretch transition-opacity duration-300"
              style={{
                opacity: scrollProgress,
                width: merged ? "auto" : 0,
                /** Hidden only while collapsed; `visible` when merged so account menu isn’t clipped. */
                overflow: merged ? "visible" : "hidden",
              }}
            >
              {isReady && (!isLoggedIn || user?.customerId) ? (
                <NavLoyaltyPoints compact />
              ) : null}
              <div className="flex items-center gap-2 shrink-0">
                <NotificationsBell variant="nav" />
                <NavLoginOrAccount
                  loginClassName="bg-[#BD2226] text-white font-semibold text-sm px-5 py-2 rounded-lg hover:bg-[#C00322] transition-colors shrink-0 text-center cursor-pointer"
                  openLoginDialog={openNavLoginDialog}
                  onLoginClick={() => {
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      pageName: "DESKTOP HEADER",
                      pageCategory: "DESKTOP HEADER",
                      eventCategory: GTMSubEvents.Click,
                      eventLabel: "DESKTOP HEADER > LOGIN CLICK",
                    });
                  }}
                />
              </div>
            </div>
          </div>

          {/* Mobile merged: hamburger + logo + login - fades in with scroll */}
          <div
            className={`md:hidden flex items-center justify-between transition-[opacity,padding-block] duration-300 ease-out ${merged ? "py-3 opacity-100" : "pointer-events-none h-0 overflow-hidden py-0 opacity-0"}`}
          >
              <button type="button" onClick={open} className="p-2 -ml-2 rounded-lg text-[#130F0E] hover:bg-[#F4F4F4] transition-colors shrink-0" aria-label="Open menu">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <LocaleLink href="/" className="flex items-center shrink-0">
                <img
                  src={SITE_LOGO_SRC}
                  alt={SITE_LOGO_ALT}
                  className="h-8 w-[100px] max-w-[260px] object-contain object-left md:w-auto"
                  width={260}
                  height={40}
                />
              </LocaleLink>
              <div className="flex items-center gap-2 shrink-0">
                {isReady && (!isLoggedIn || user?.customerId) ? (
                  <NavLoyaltyPoints compact />
                ) : null}
                <NotificationsBell variant="nav" />
                <NavLoginOrAccount
                  loginClassName="bg-[#BD2226] text-white font-semibold text-sm px-5 py-2 rounded-lg hover:bg-[#C00322] transition-colors shrink-0 text-center cursor-pointer"
                  openLoginDialog={openNavLoginDialog}
                  onLoginClick={() => {
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      pageName: "MOBILE HEADER",
                      pageCategory: "MOBILE HEADER",
                      eventCategory: GTMSubEvents.Click,
                      eventLabel: "MOBILE HEADER > LOGIN CLICK",
                    });
                  }}
                />
              </div>
            </div>
        </div>
      </nav>

      {/* Mobile menu slider */}
      <div className={`fixed inset-0 z-[100] md:hidden transition-opacity duration-300 ${mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-[#130F0E]/50" onClick={closeMobileMenu} aria-hidden />
        <div
          className={`absolute top-0 h-full w-[min(320px,85vw)] max-w-full bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${isRtl ? "left-0" : "right-0"} ${mobileMenuOpen ? "translate-x-0" : isRtl ? "-translate-x-full" : "translate-x-full"}`}
        >
          <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
            <span className="font-bold text-[15px] text-[#3A3A3C]">{t("nav.menu")}</span>
            <button type="button" 
            onClick={() => {
              closeMobileMenu();
              PushDataToGTM(GTMEvents.DataLayerUpdate, {
                pageName: "MOBILE HEADER",
                pageCategory: "MOBILE MENU",
                eventCategory: GTMSubEvents.Click,
                eventLabel: "MOBILE HEADER > CLOSE MENU",
              });
            }}
             className="p-2 -mr-2 rounded-lg text-[#6B7280] hover:bg-[#F4F4F4] hover:text-[#130F0E] transition-colors" aria-label="Close menu">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {menuItemKeys.map((item) => {
              const mega = megaMenuItemsByKey[item.key];
              if (item.key === "home" || item.key === "news" || !mega?.length) {
                return (
                  <LocaleLink
                    key={item.key}
                    href={item.href}
                    onClick={() => {
                      closeMobileMenu();
                      PushDataToGTM(GTMEvents.DataLayerUpdate, {
                        pageName: "MOBILE HEADER",
                        pageCategory: "MOBILE MENU",
                        eventCategory: GTMSubEvents.Click,
                        eventLabel: `MOBILE HEADER > ${item.key.toUpperCase()}`,
                      });
                    }}
                    className="block border-b border-[#F0F0F0] text-[15px] px-6 py-3.5 text-[#3A3A3C] font-normal transition-colors last:border-b-0 hover:bg-[#F4F4F4] hover:text-[#BD2226]"
                  >
                    {t(`nav.${item.key}`)}
                  </LocaleLink>
                );
              }
              const expanded = mobileNavExpandedKey === item.key;
              return (
                <div key={item.key} className="border-b border-[#F0F0F0] last:border-b-0">
                  <button
                    type="button"
                    id={`nav-mobile-${item.key}-btn`}
                    aria-expanded={expanded}
                    aria-controls={`nav-mobile-${item.key}-panel`}
                    onClick={() => {
                      setMobileNavExpandedKey((k) => (k === item.key ? null : item.key));
                      PushDataToGTM(GTMEvents.DataLayerUpdate, {
                        pageName: "MOBILE HEADER",
                        pageCategory: "MOBILE MENU",
                        eventCategory: GTMSubEvents.Click,
                        eventLabel: `MOBILE HEADER > ${item.key.toUpperCase()}`,
                      });
                    }}
                    className="flex w-full text-[15px] items-center justify-between gap-3 px-6 py-3.5 text-start font-normal text-[#3A3A3C] transition-colors hover:bg-[#F4F4F4]"
                  >
                    <span>{t(`nav.${item.key}`)}</span>
                    <MobileNavChevron expanded={expanded} />
                  </button>
                  <div
                    id={`nav-mobile-${item.key}-panel`}
                    role="region"
                    aria-labelledby={`nav-mobile-${item.key}-btn`}
                    hidden={!expanded}
                    className={expanded ? "border-t border-[#F4F4F4] bg-[#FAFAFA]" : "hidden"}
                  >
                    <ul className="py-1 pb-2" role="list">
                      {mega.map(({ href, icon, titleKey, descKey, label }) => (
                        <li key={`${href}-${titleKey}`}>
                          <LocaleLink
                            href={href}
                            onClick={() => {
                              closeMobileMenu();
                              PushDataToGTM(GTMEvents.DataLayerUpdate, {
                                pageName: "MOBILE HEADER",
                                pageCategory: "MOBILE MENU",
                                eventCategory: GTMSubEvents.Click,
                                eventLabel: `MOBILE HEADER > ${label.toUpperCase()}`,
                              });
                            }}
                            className={`flex gap-3 px-6 py-3 transition-colors hover:bg-[#F0F0F0] focus-visible:bg-[#F0F0F0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#DD0428]/25 ${isRtl ? "flex-row-reverse" : ""}`}
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#BD2226]/10 text-[#BD2226]">
                              <MegaIcon id={icon} className="h-5 w-5" />
                            </span>
                            <span className="min-w-0 flex-1 text-start">
                              <span className="block text-[13px] font-semibold text-[#3A3A3C]">{t(titleKey)}</span>
                              <span className="mt-0.5 block text-[11px] leading-snug text-[#6B7280]">{t(descKey)}</span>
                            </span>
                          </LocaleLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
            {isReady && isLoggedIn ? (
              <div className="">
                <LocaleLink
                  href="/my-account"
                  onClick={() => {
                    closeMobileMenu();
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      pageName: "MOBILE HEADER",
                      pageCategory: "MOBILE MENU",
                      eventCategory: GTMSubEvents.Click,
                      eventLabel: "MOBILE HEADER > MY ACCOUNT CLICK",
                    });
                  }}
                  className="block px-6 py-3 text-[15px] text-[#3A3A3C] font-normal hover:bg-[#F4F4F4] hover:text-[#BD2226] transition-colors"
                >
                  {t("nav.myAccount")}
                </LocaleLink>
                <button
                  type="button"
                  onClick={() => {
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      pageName: "MOBILE HEADER",
                      pageCategory: "MOBILE MENU",
                      eventCategory: GTMSubEvents.Click,
                      eventLabel: "MOBILE HEADER > LOGOUT CLICK",
                    });
                    logout();
                    closeMobileMenu();
                    // router.push(withLocalePrefix(locale, "/"));
                  }}
                  className="block w-full border-t text-[15px] border-[#E5E5E5] text-start px-6 py-3 text-[#BD2226] font-semibold hover:bg-[#F4F4F4] transition-colors"
                >
                  {t("nav.logout")}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        returnUrl={pathname || "/"}
        onLoggedIn={() => {
          closeMobileMenu();
        }}
        pageName={!isMd ? "MOBILE HEADER" : "DESKTOP HEADER"}
        pageCategory={!isMd ? "MOBILE HEADER" : "DESKTOP HEADER"}
        eventCategory={GTMSubEvents.Click}
      />
      </div>
    </>
  );
}
